import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import CircuitLayout from "../../../components/CircuitLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Button, Col, Row, Alert } from "react-bootstrap";
import {
  faCalendar,
  faDotCircle,
  faFlagCheckered,
  faLocationDot,
  faMapLocation,
  faMapLocationDot,
  faQuestionCircle,
  faStopwatch,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import DateFormat from "../../../components/DateFormat";
import LoadingContent from "../../../components/LoadingContent";
import Title from "../../../components/Admin/Title";
import { getAbbreviation } from "../../../utils/Standings";
import "../../../assets/style/imgnewsrally_v1.css";
import Flag from "../../../components/Flag";

interface GPData {
  transformedStages: any;
  values: any;
}

const ImageGPRallyGenerator = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<GPData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const componentRef = useRef<HTMLDivElement>(null);

  const [update, setUpdate] = useState(false);
  const [grandPrixEventId, setGrandPrixEventId] = useState(-1);
  const [nextGrandsPrix, setNextGrandsPrix] = useState<
    {
      name: string;
      id: number;
      type_race: string;
      type_races: number;
      type_championship: number;
    }[]
  >();

  const [customBgImages, setCustomBgImages] = useState<string[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleCustomImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setImageError(null);

    if (files && files.length > 0) {
      const requiredCount = data ? data.transformedStages.length : 9;

      if (files.length !== requiredCount) {
        setImageError(
          `Debes seleccionar exactamente ${requiredCount} imágenes (has seleccionado ${files.length}).`,
        );
        setCustomBgImages([]);
        return;
      }

      const imageUrls = Array.from(files).map((file) =>
        URL.createObjectURL(file),
      );
      setCustomBgImages(imageUrls);
    } else {
      setCustomBgImages([]);
    }
  };

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getLastGrandsPrix`,
        {
          params: { type_races: 1 },
        },
      )
      .then(({ data }) => {
        setNextGrandsPrix(data);
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, [update]);

  useEffect(() => {
    if (
      grandPrixEventId !== null &&
      grandPrixEventId !== undefined &&
      grandPrixEventId !== -1
    ) {
      setIsLoading(true);
      setData(null);
      setGeneratedImageUrl(null);
      setError(null);
      setCustomBgImages([]);

      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";

      axios
        .get(`${API_BASE}/api/grandprix/getNewsRallyGenerateInfo`, {
          params: { grand_prix_event_id: grandPrixEventId },
        })
        .then(({ data }) => {
          setData(data);
          console.log(data.values.length);
          if (data.values.length === 0) setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching GP info:", err);
          setError("No se pudo cargar la información del Gran Premio.");
          setIsLoading(false);
        });
    }
  }, [grandPrixEventId]);
  // Reemplaza tus useEffects de generación de imagen actuales por estos:

  const handleGenerateImage = useCallback(() => {
    if (componentRef.current === null) return;

    // Pequeño timeout interno para asegurar el pintado final del DOM oculto
    setTimeout(() => {
      if (!componentRef.current) return;

      toPng(componentRef.current, {
        quality: 1.0,
        canvasWidth: 1920,
        canvasHeight: 1080,
        style: {
          overflow: "hidden",
        },
      })
        .then((dataUrl: string) => {
          setGeneratedImageUrl(dataUrl);
        })
        .catch((err) => {
          console.error("Error generating image:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 600); // 600ms de margen para que CircuitLayout y las banderas terminen de pintar
  }, [componentRef]);

  useEffect(() => {
    if (data !== null && data.values.length > 0) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 400); // Espera inicial al cambiar de datos antes de disparar

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage, customBgImages]);

  useEffect(() => {
    if (data !== null && data.values.length > 0) {
      const rafId = requestAnimationFrame(() => {
        handleGenerateImage();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [data, handleGenerateImage, customBgImages]);

  useEffect(() => {
    if (data !== null) {
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage]);

  const getWeatherIcon = (weatherString: string) => {
    if (weatherString == null) return "";
    const text = weatherString.toLowerCase();

    if (
      !text.includes("nosnow") &&
      (text.includes("heavysnow") ||
        text.includes("lightsnow") ||
        text.includes("snow"))
    ) {
      return "🌨️";
    }

    // 1. La lluvia va primero, pero bloqueando la palabra "norain" para que no salte por error
    if (
      !text.includes("norain") &&
      (text.includes("heavyrain") ||
        text.includes("lightrain") ||
        text.includes("rain"))
    ) {
      return "🌧️";
    }

    // 2. Las nubes van después. Si hay "HeavyRain HeavyCloud", como ya pasó el filtro de arriba, aquí cogerá la nube si no lloviera, pero si llueve se queda con la lluvia.
    if (
      text.includes("partcloud") ||
      text.includes("lightcloud") ||
      text.includes("heavycloud") ||
      text.includes("cloud")
    ) {
      return "☁️";
    }

    if (
      text.includes("heavyfog") ||
      text.includes("lightfog") ||
      text.includes("fog")
    ) {
      return "☁️";
    }
    if (text.includes("evening")) return "🌙";

    if (
      text.includes("crisp") ||
      text.includes("hazy") ||
      text.includes("clear")
    ) {
      return "☀️";
    }

    return "🌡️";
  };
  if (nextGrandsPrix == null) return <LoadingContent />;
  const totalStages = data ? data.transformedStages.length : 0;
  return (
    <div className="full-height padding-md app-container flex-center-top page-transition noverflow">
      <div className="form-container-md">
        <Title text="Image GP Rally Generator" />
        <small className="mandatory-fields">(*) Required fields</small>
        <Form>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>Grand Prix (*)</Form.Label>
              <Form.Select
                name="grand_prix"
                required
                onChange={(e) => {
                  setGrandPrixEventId(parseInt(e.target.value));
                }}
              >
                <option value="-1">Select a Grand Prix...</option>
                {nextGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>

          {data && (
            <Row className="mb-3">
              <Form.Group className="form-group" as={Col} md="12">
                <Form.Label>
                  Custom Background Images
                  <span
                    className="profile-bubble"
                    data-tooltip={`Upload exactly ${totalStages} images to customize each stage card background.`}
                    data-flow="right"
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="label-icon"
                    />
                  </span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleCustomImagesChange}
                />
                {imageError && (
                  <Alert variant="danger" className="mt-2 py-1">
                    {imageError}
                  </Alert>
                )}
              </Form.Group>
            </Row>
          )}
        </Form>
        {isLoading && <LoadingContent />}
        {data != null && data.values.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: "-9999px",
              pointerEvents: "none",
            }}
          >
            {/* Contenedor principal que envuelve TODO lo que se va a exportar en la imagen */}
            <div
              ref={componentRef}
              style={{ background: "#111", padding: "20px" }}
            >
              <div className="nig-container cont-r1">
                <div
                  className="nig-background-overlay"
                  style={
                    {
                      "--bg-gp-image": `url(/assets/ImagesGP/${data.values[0].id}.jpg)`,
                    } as React.CSSProperties
                  }
                />
              </div>

              <div className="dashboard-grid">
                {data.transformedStages.map((stageItem: any, index: any) => {
                  const stageDetails = {
                    name: `${data.values[index].circuit}`,
                    weather: `${getWeatherIcon(data.values[index].weather)} ${data.values[index].weather}`,
                    distance: `${data.values[index].length.toFixed(1)} km`,
                  };

                  const hasCustomImages = customBgImages.length === totalStages;

                  return (
                    <div
                      className="stage-card"
                      key={index}
                      style={{ position: "relative", overflow: "hidden" }}
                    >
                      {hasCustomImages && (
                        <div
                          style={{
                            position: "absolute",
                            top: "-10px",
                            left: "-10px",
                            right: "-10px",
                            bottom: "-10px",
                            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${customBgImages[index]})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(2px)",
                            zIndex: 0,
                          }}
                        />
                      )}

                      <div
                        className="stage-header"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <span className="stage-badge">
                          S {String(data.values[index].stage).padStart(2, "0")}
                        </span>
                        <div className="stage-info">
                          <h2>{stageDetails.name.toUpperCase()}</h2>
                          <p className="newsimg-weather">
                            {stageDetails.weather}{" "}
                          </p>
                        </div>
                      </div>

                      <div
                        className="stage-body"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <div className="stage-map">
                          <div id="next-gp-container3">
                            <CircuitLayout
                              circuit_id={data.values[index].circuit_id}
                              id_canvas={`circuit-${data.values[index].circuit_id}`}
                              strokeWidth={2}
                              color={"#00e5ff"}
                            />
                          </div>
                          <span className="distance">
                            {stageDetails.distance}
                          </span>
                        </div>

                        <div className="stage-tables">
                          {Object.entries(data.transformedStages[index].R1).map(
                            (
                              [categoryName, drivers]: [string, any],
                              catIndex,
                            ) => (
                              <div className="table-column" key={catIndex}>
                                <h3
                                  className={`category-title ${categoryName.toLowerCase().replace(/\s+/g, "")}`}
                                  style={{
                                    backgroundColor: drivers[0].class_color,
                                  }}
                                >
                                  {categoryName.toUpperCase()}
                                </h3>
                                <ol>
                                  {(
                                    drivers as Array<{
                                      name: string;
                                      driver_flag: string;
                                    }>
                                  ).map((driver, driverIndex) => (
                                    <li key={driverIndex}>
                                      <Flag
                                        flag_code={driver.driver_flag}
                                        name={""}
                                      />{" "}
                                      {getAbbreviation(driver.name)}
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {generatedImageUrl && (
          <div className="nig-preview-container mt-4">
            <img
              src={generatedImageUrl}
              alt="Preview Noticia GP"
              className="nig-preview-img"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
            <div className="mt-3">
              <Button variant="primary">
                <a
                  href={generatedImageUrl}
                  download={`GP_Results.png`}
                  style={{ color: "white", textDecoration: "none" }}
                >
                  Descargar Imagen Final
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGPRallyGenerator;
